import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../../components/ui/Layout';
import BreadcrumbNavigation from '../../../components/ui/BreadcrumbNavigation';
import ShareButton from '../../../components/ui/ShareButton';
import { db } from '../../../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { SITE_URL, SITE_NAME } from '../../../constants/site';
import { useAuth } from '../../../components/context/AuthContext';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('Supermarkets');

const SupermarketImage = ({ imagePath, altText }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  useEffect(() => {
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:')) {
      const imageRef = ref(storage, imagePath);
      getDownloadURL(imageRef)
        .then(setImageUrl)
        .catch(err => logger.error('[SupermarketImage] Error fetching image URL:', imagePath, err));
    } else {
      setImageUrl('');
    }
  }, [imagePath, storage]);

  if (!imageUrl) {
    return (
      <div
        className="card-img-top d-flex align-items-center justify-content-center"
        style={{ height: '200px', backgroundColor: '#e9ecef' }}
      >
        <span className="text-muted">No Image</span>
      </div>
    );
  }
  return (
    <img
      src={imageUrl}
      className="card-img-top"
      alt={altText}
      style={{ height: '200px', objectFit: 'cover' }}
    />
  );
};

const formatOpeningHours = (openingHours) => {
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    return null;
  }
  return openingHours.map((h, idx) => (
    <div key={idx} className="small text-muted">
      {h.day}: {h.hours}
    </div>
  ));
};

export default function Supermarkets() {
  const { currentUser } = useAuth();
  const [supermarkets, setSupermarkets] = useState([]);
  const [ratingsBySupermarket, setRatingsBySupermarket] = useState({});
  const [loading, setLoading] = useState(true);

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { label: 'Hardware stores' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch supermarkets (public) - required for page to work
        const supermarketsSnapshot = await getDocs(
          query(collection(db, 'Supermarkets'), orderBy('display_name'))
        );
        const fetchedSupermarkets = supermarketsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSupermarkets(fetchedSupermarkets);

        // Fetch reviews (public) for ratings - optional, page works without them
        try {
          const reviewsSnapshot = await getDocs(collection(db, 'SupermarketReviews'));
          const ratings = {};
          reviewsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const sid = data.supermarketId;
            if (!sid) return;
            if (!ratings[sid]) ratings[sid] = { sum: 0, count: 0 };
            if (typeof data.rating === 'number' && !data.hidden) {
              ratings[sid].sum += data.rating;
              ratings[sid].count += 1;
            }
          });
          setRatingsBySupermarket(ratings);
        } catch (reviewsErr) {
          logger.warn('Could not fetch reviews (ratings may be hidden):', reviewsErr);
        }
      } catch (err) {
        logger.error('Error fetching supermarkets:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getAverageRating = (supermarketId) => {
    const r = ratingsBySupermarket[supermarketId];
    if (!r || r.count === 0) return null;
    return (r.sum / r.count).toFixed(1);
  };

  const getReviewCount = (supermarketId) => {
    const r = ratingsBySupermarket[supermarketId];
    return r?.count ?? 0;
  };

  if (loading) {
    return (
      <Layout>
        <Container className="mt-4 text-center py-5">
          <Spinner animation="border" role="status" />
          <p className="mt-2 text-muted">Loading hardware stores...</p>
        </Container>
      </Layout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Hardware stores | ${SITE_NAME}`}</title>
        <meta
          name="description"
          content="Browse all hardware stores. Find addresses, opening hours, and ratings. Shop groceries online with delivery and pickup options."
        />
      </Helmet>
      <Layout>
        <Container className="mt-4 px-3">
          <BreadcrumbNavigation items={breadcrumbItems} />
          <h1 className="mb-4 text-dark-green">All hardware stores</h1>

          {supermarkets.length === 0 ? (
            <p className="text-muted">No hardware stores available at the moment.</p>
          ) : (
            <Row xs={1} md={2} lg={3} className="g-4">
              {supermarkets.map(supermarket => {
                const productsUrl = `/hardware/${supermarket.id}/products`;
                const shareUrl = supermarket.seo_name
                  ? `${SITE_URL}/hardware/${supermarket.id}/products/${supermarket.seo_name}`
                  : `${SITE_URL}/hardware/${supermarket.id}/products`;
                const avgRating = getAverageRating(supermarket.id);
                const reviewCount = getReviewCount(supermarket.id);
                const openingHours = supermarket.opening_hours;

                return (
                  <Col key={supermarket.id}>
                    <Card className="h-100 supermarket-card-custom">
                      <Link to={productsUrl} style={{ textDecoration: 'none', color: 'inherit' }}>
                        {supermarket.media && supermarket.media.length > 0 && supermarket.media[0].url ? (
                          <SupermarketImage
                            imagePath={supermarket.media[0].url}
                            altText={supermarket.display_name}
                          />
                        ) : (
                          <div
                            className="card-img-top d-flex align-items-center justify-content-center"
                            style={{ height: '200px', backgroundColor: '#e9ecef' }}
                          >
                            <span className="text-muted">No Image</span>
                          </div>
                        )}
                        <Card.Body>
                          <Card.Title className="text-center">
                            {supermarket.display_name || 'Unnamed Supermarket'}
                          </Card.Title>
                          {supermarket.address && (
                            <Card.Text className="text-muted small mb-2">
                              <i className="fas fa-map-marker-alt me-1" />
                              {supermarket.address}
                            </Card.Text>
                          )}
                          {openingHours && openingHours.length > 0 && (
                            <div className="mb-2">
                              <span className="small fw-semibold">Opening hours:</span>
                              {formatOpeningHours(openingHours)}
                            </div>
                          )}
                          {currentUser && (
                            <div className="d-flex align-items-center gap-2">
                              {avgRating ? (
                                <>
                                  <span className="text-warning">
                                    {'★'.repeat(Math.round(parseFloat(avgRating)))}
                                    {'☆'.repeat(5 - Math.round(parseFloat(avgRating)))}
                                  </span>
                                  <span className="small text-muted">
                                    {avgRating} ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                                  </span>
                                </>
                              ) : (
                                <span className="small text-muted">No ratings yet</span>
                              )}
                            </div>
                          )}
                        </Card.Body>
                      </Link>
                      <Card.Footer className="bg-transparent border-top-0 pt-0">
                        <div className="d-flex justify-content-between align-items-center">
                          <Link to={productsUrl} className="btn btn-primary btn-sm">
                            View Products
                          </Link>
                          <ShareButton
                            url={shareUrl}
                            title={`${supermarket.display_name} - Products | ${SITE_NAME}`}
                            text={`Shop groceries from ${supermarket.display_name}`}
                            variant="outline-secondary"
                            size="sm"
                            showLabel={false}
                          />
                        </div>
                      </Card.Footer>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </Layout>
    </>
  );
}
